import { z } from 'zod';
import { POLICY_EVENT_TYPES } from './policy.js';
/**
 * What a policy condition can actually read.
 *
 * A policy names a dotted field path and the evaluator resolves it against the
 * object `buildEvalContext` produces. Nothing validated that path, so a
 * plausible-but-wrong one — `event.measurements.speed`, say — was never an
 * error: it resolved to `undefined`, matched nothing, and the policy sat there
 * looking configured. That is the same shape of defect as the free-text
 * `eventType` this package already fixed.
 *
 * This catalogue is DERIVED, not invented. Three sources, and each is a single
 * enumerable place:
 *
 *  - the context literal in `buildEvalContext` (`intelligence.service.ts`),
 *    which is where `incident.*`, `context.*`, `vehicle.*` and the `event`
 *    envelope come from;
 *  - the provider adapter's per-event-type `switch`
 *    (`provider-integration/ajnaview/adapter.ts`), for the measurements and
 *    attributes of provider events;
 *  - the CAN telemetry rules (`domain/telemetry/rules.ts`), for the events
 *    derived from samples rather than received.
 *
 * `apps/api/test/policy-fields.test.ts` keeps it in step with all three by
 * driving the real adapter and comparing, because a catalogue that silently
 * drifts is worse than none — it would refuse a field that works.
 */
/**
 * WHERE a field can be read, which is not the same everywhere.
 *
 * `risk` is merged into the context ONLY for action rules
 * (`intelligence.service.ts` builds `{ ...evalContext, risk }` just before
 * `selectActionRule`). Risk is calculated FROM the score bands, so it cannot
 * exist while they are being evaluated. A `risk.score` condition in the
 * top-level `condition`, a score band or a modifier therefore resolves to
 * `undefined` and silently never matches — which reads as a policy that is
 * simply never triggered.
 */
export const FIELD_SCOPES = ['ANYWHERE', 'ACTION_RULES_ONLY'];
const HARSH = ['HARSH_BRAKING', 'HARSH_ACCELERATION', 'SHARP_TURN'];
const SEATBELT = ['DRIVER_SEATBELT_UNFASTENED', 'PASSENGER_SEATBELT_UNFASTENED'];
const ALARM = ['ALARM_WHILE_MOVING', 'ALARM_WHILE_STATIONARY'];
const ADBLUE = ['ADBLUE_REFILL_REQUIRED'];
const PTO = ['PTO_ENGAGED_WHILE_MOVING'];
/** Kept in the order an operator thinks in: the incident, then the event, then context. */
export const POLICY_FIELDS = [
    // ── the incident this event landed on ─────────────────────────────────────
    {
        path: 'incident.type',
        label: 'incident type',
        type: 'enum',
        scope: 'ANYWHERE',
        options: [
            'HIGH_RISK_DRIVER_BEHAVIOUR',
            'COMPLIANCE_RISK',
            'ASSET_AVAILABILITY_RISK',
            'VEHICLE_HEALTH_RISK',
            'FUEL_LOSS_RISK',
            'SAFETY_VIOLATION_RISK',
            'SECURITY_RISK',
        ],
    },
    {
        path: 'incident.occurrenceCount',
        label: 'times this has happened on this incident',
        type: 'number',
        scope: 'ANYWHERE',
    },
    {
        path: 'incident.status',
        label: 'incident status',
        type: 'enum',
        scope: 'ANYWHERE',
        options: ['OPEN', 'ACKNOWLEDGED', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'],
    },
    {
        path: 'incident.currentRiskScore',
        label: 'the incident’s risk score so far',
        type: 'number',
        scope: 'ANYWHERE',
    },
    // ── the event envelope ────────────────────────────────────────────────────
    {
        path: 'event.eventType',
        label: 'event type',
        type: 'enum',
        scope: 'ANYWHERE',
        options: POLICY_EVENT_TYPES,
    },
    {
        path: 'event.severity',
        label: 'event severity',
        type: 'enum',
        scope: 'ANYWHERE',
        options: ['INFO', 'WARNING', 'CRITICAL'],
    },
    // ── measurements, from the provider adapter's per-type switch ─────────────
    {
        path: 'event.measurements.speedKph',
        label: 'speed',
        type: 'number',
        unit: ' km/h',
        scope: 'ANYWHERE',
        eventTypes: ['OVER_SPEED', ...HARSH, ...SEATBELT, ...ALARM, ...PTO, 'TOW_ALERT'],
    },
    {
        path: 'event.measurements.speedLimitKph',
        label: 'speed limit',
        type: 'number',
        unit: ' km/h',
        scope: 'ANYWHERE',
        eventTypes: ['OVER_SPEED'],
    },
    {
        path: 'event.measurements.overByKph',
        label: 'speed over the limit',
        type: 'number',
        unit: ' km/h',
        scope: 'ANYWHERE',
        eventTypes: ['OVER_SPEED'],
    },
    {
        path: 'event.measurements.durationSeconds',
        label: 'duration',
        type: 'number',
        unit: ' seconds',
        scope: 'ANYWHERE',
        eventTypes: ['OVER_SPEED', 'TOW_ALERT'],
    },
    {
        path: 'event.measurements.voltage',
        label: 'battery voltage',
        type: 'number',
        unit: 'V',
        scope: 'ANYWHERE',
        eventTypes: ['BATTERY_POWER_ZERO'],
    },
    {
        path: 'event.measurements.peakG',
        label: 'peak g-force',
        type: 'number',
        unit: 'g',
        scope: 'ANYWHERE',
        eventTypes: HARSH,
    },
    {
        path: 'event.measurements.durationMs',
        label: 'duration',
        type: 'number',
        unit: ' ms',
        scope: 'ANYWHERE',
        eventTypes: HARSH,
    },
    /*
     * A flagged fuel-card transaction. Every one of these is the PROVIDER's
     * measurement of a purchase, carried so a tenant can band on it — OIP holds
     * no card transactions of its own.
     */
    {
        path: 'event.measurements.volumeLitres',
        label: 'fuel purchased',
        type: 'number',
        unit: ' litres',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_CARD_FRAUD_SUSPECTED'],
    },
    {
        /*
         * ⚠️ The first money in the platform, and it is TRANSACTION-scoped. It is
         * what the card was charged, not a fuel price OIP holds or a cost model it
         * derives — nothing may extrapolate a fleet spend from it.
         */
        path: 'event.measurements.transactionAmount',
        label: 'amount charged',
        type: 'number',
        unit: '',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_CARD_FRAUD_SUSPECTED'],
    },
    {
        /*
         * ⚠️ ZERO is the finding here, not an absence — fuel bought and the tank
         * unmoved. A policy comparing `<= 0` is the intended use, which is only
         * meaningful because absent is left absent rather than defaulted to zero.
         */
        path: 'event.measurements.fuelChangeLitres',
        label: 'tank level change',
        type: 'number',
        unit: ' litres',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_CARD_FRAUD_SUSPECTED'],
    },
    {
        path: 'event.measurements.distanceToSiteMetres',
        label: 'distance from the site',
        type: 'number',
        unit: ' m',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_CARD_FRAUD_SUSPECTED'],
    },
    {
        path: 'event.attributes.cardLast4',
        label: 'card ending',
        type: 'string',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_CARD_FRAUD_SUSPECTED'],
    },
    {
        path: 'event.attributes.fraudReasons',
        label: 'reasons the provider gave',
        type: 'string',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_CARD_FRAUD_SUSPECTED'],
    },
    {
        path: 'event.measurements.litresLost',
        label: 'fuel lost',
        type: 'number',
        unit: ' litres',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_DROP'],
    },
    /*
     * ⚠️ **THE SAME LOSS, MEASURED TWO WAYS, AND THEY MUST NEVER SHARE A LABEL.**
     *
     * `litresLost` above is the PROVIDER's figure from a calibrated sender. These three
     * are OIP's, derived by the telemetry rule from the percentage level — for the
     * fleets whose provider sends no drop at all, which on the development database was
     * very nearly all of them: 2 `FUEL_DROP` events against 7,276 hourly fuel-level rows
     * across 30 vehicles.
     *
     * They are separate PATHS with their own labels and their own unit because percent
     * is not litres without a tank capacity OIP does not hold, and a policy author
     * banding on "fuel lost" must know which quantity they are comparing against. This
     * is the `durationMs`/`durationSeconds` lesson — two fields under one label, a
     * factor of a thousand apart — and here the factor is whatever the tank holds.
     */
    {
        path: 'event.measurements.fuelPctLost',
        label: 'fuel lost (share of tank)',
        type: 'number',
        unit: ' percentage points',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_DROP'],
    },
    {
        /*
         * ⚠️ **A DIFFERENT PATH FROM `litresLost`, and the label says ESTIMATED.** That one is
         * the provider's measurement from a calibrated sender; this is OIP converting a
         * percentage drop with the tank capacity a customer typed. A policy author banding on
         * "fuel lost" must know which they are comparing against — and only one of them exists
         * on any given event.
         *
         * Present only where the vehicle has a capacity recorded. Absent is not zero: every
         * vehicle carries none until somebody types one.
         */
        path: 'event.measurements.litresLostEstimated',
        label: 'fuel lost (estimated from the tank level)',
        type: 'number',
        unit: ' litres',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_DROP'],
    },
    {
        path: 'event.measurements.fuelPctBefore',
        label: 'tank level before the drop',
        type: 'number',
        unit: '%',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_DROP'],
    },
    {
        path: 'event.measurements.fuelPctAfter',
        label: 'tank level after the drop',
        type: 'number',
        unit: '%',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_DROP'],
    },
    {
        path: 'event.measurements.litresAdded',
        label: 'fuel added',
        type: 'number',
        unit: ' litres',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_INCREASE'],
    },
    {
        path: 'event.measurements.litresBefore',
        label: 'fuel before',
        type: 'number',
        unit: ' litres',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_DROP', 'FUEL_INCREASE'],
    },
    /*
     * Tow. `distanceMetres` is how far the vehicle has been moved since the alert
     * — the number that separates a nudge in a yard from a theft in progress —
     * and it is metres rather than kilometres because the useful range is tens.
     */
    {
        path: 'event.measurements.distanceMetres',
        label: 'distance moved',
        type: 'number',
        unit: ' metres',
        scope: 'ANYWHERE',
        eventTypes: ['TOW_ALERT'],
    },
    {
        path: 'event.measurements.litresAfter',
        label: 'fuel after',
        type: 'number',
        unit: ' litres',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_DROP', 'FUEL_INCREASE'],
    },
    {
        path: 'event.measurements.windowMinutes',
        label: 'over a window of',
        type: 'number',
        unit: ' minutes',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_DROP'],
    },
    {
        path: 'event.measurements.sustainedSeconds',
        label: 'sustained for',
        type: 'number',
        unit: ' seconds',
        scope: 'ANYWHERE',
        /*
         * ⚠️ ONE entry per path — `BY_PATH` is keyed by it, so a second entry for
         * the same path silently SHADOWS the first and every policy reading it via
         * the older event type starts failing the write guard. PTO reuses the
         * seatbelt fold, so it widens this list rather than adding a row.
         */
        eventTypes: [...SEATBELT, ...PTO],
    },
    {
        path: 'event.measurements.defWarningLevel',
        label: 'AdBlue warning level',
        type: 'number',
        /*
         * No unit — it is an ORDERED CODE, 0 none .. 3 severe, not a quantity. A
         * unit here would invite reading it as a percentage, and the one thing this
         * field must never be confused with is `defLevelPct` two lines down.
         */
        scope: 'ANYWHERE',
        eventTypes: ADBLUE,
    },
    {
        path: 'event.measurements.defLevelPct',
        label: 'AdBlue remaining',
        type: 'number',
        unit: '%',
        scope: 'ANYWHERE',
        eventTypes: ADBLUE,
    },
    // ── attributes ────────────────────────────────────────────────────────────
    {
        /**
         * Which band a drivers'-hours reading fell in.
         *
         * The rule's OWN decision, made where the device's remaining time or the
         * tenant's configured limit was read — so a policy reading it back cannot
         * disagree with the rule that raised the event. Banding on the incident's
         * risk level instead resolves to nothing: the evaluation context has no
         * `riskLevel`, and the condition would silently never match.
         *
         * `MAX` appears only on the daily limit, where the extended maximum is a
         * third rung above the ordinary one.
         */
        path: 'event.attributes.band',
        label: 'how close to the limit',
        type: 'enum',
        scope: 'ANYWHERE',
        options: ['APPROACHING', 'REACHED', 'MAX'],
        eventTypes: [
            'DRIVERS_HOURS_BREAK_DUE',
            'DRIVERS_HOURS_REST_DUE',
            'DRIVERS_HOURS_DAILY_LIMIT',
            'DRIVERS_HOURS_WEEKLY_LIMIT',
        ],
    },
    /*
     * ─────────────────────────────────────────────────────────────────────────
     * The four drivers'-hours counters.
     *
     * ⚠️ **THESE WERE EMITTED AND UNCATALOGUED, and `tachoTimeToBreakMin` was the
     * MOST FREQUENTLY RECORDED MEASUREMENT ON THE PLATFORM** — 58 events on the
     * development database, rendering with no label and no unit because the
     * measurements column falls back to the raw key when the catalogue has none.
     *
     * ⚠️ They fell between the two halves of the guard, which is the part worth
     * understanding. `policy-fields.test.ts` checks the adapter's emitted keys
     * against the catalogue, and these are produced by the TELEMETRY RULES rather
     * than the adapter — so that check's `catch { continue }` skipped them and
     * deferred to the seeded-policy check, which covers fields a policy READS.
     * These are read by no policy: the shipped ones band on
     * `event.attributes.band` above, deliberately, because the evaluation context
     * carries no `riskLevel`. Emitted by a rule and read by nothing is a gap
     * neither direction of the check could see, and
     * `emitted-measurements.test.ts` is the guard that closes it.
     *
     * ⚠️ **MINUTES, every one of them**, which is why the unit is not optional:
     * `durationMs` and `durationSeconds` already share the label "duration" and
     * differ by a thousand, and a counter whose unit a reader has to guess is the
     * same failure one field along.
     *
     * Each is carried by exactly ONE event type, so there is no shadowing risk —
     * but the narrowing is stated anyway, because it is what stops a drivers'-hours
     * field being offered on an over-speed policy.
     * ─────────────────────────────────────────────────────────────────────────
     */
    {
        path: 'event.measurements.tachoTimeToBreakMin',
        label: 'time before a break is due',
        type: 'number',
        unit: ' minutes',
        scope: 'ANYWHERE',
        eventTypes: ['DRIVERS_HOURS_BREAK_DUE'],
    },
    {
        path: 'event.measurements.tachoTimeToDailyRestMin',
        label: 'time before daily rest is due',
        type: 'number',
        unit: ' minutes',
        scope: 'ANYWHERE',
        eventTypes: ['DRIVERS_HOURS_REST_DUE'],
    },
    {
        path: 'event.measurements.tachoDailyDriveMin',
        label: 'driving time today',
        type: 'number',
        unit: ' minutes',
        scope: 'ANYWHERE',
        eventTypes: ['DRIVERS_HOURS_DAILY_LIMIT'],
    },
    {
        path: 'event.measurements.tachoWeeklyDriveMin',
        label: 'driving time this week',
        type: 'number',
        unit: ' minutes',
        scope: 'ANYWHERE',
        eventTypes: ['DRIVERS_HOURS_WEEKLY_LIMIT'],
    },
    {
        path: 'event.attributes.geofenceId',
        label: 'the geofence',
        type: 'string',
        scope: 'ANYWHERE',
        eventTypes: ['GEOFENCE_EXIT'],
    },
    {
        path: 'event.attributes.geofenceName',
        label: 'geofence name',
        type: 'string',
        scope: 'ANYWHERE',
        eventTypes: ['GEOFENCE_EXIT'],
    },
    {
        path: 'event.attributes.direction',
        label: 'direction',
        type: 'enum',
        scope: 'ANYWHERE',
        options: ['EXIT'],
        eventTypes: ['GEOFENCE_EXIT'],
    },
    {
        path: 'event.attributes.axis',
        label: 'axis',
        type: 'enum',
        scope: 'ANYWHERE',
        options: ['LONGITUDINAL', 'LATERAL'],
        eventTypes: HARSH,
    },
    {
        path: 'event.attributes.stationary',
        label: 'vehicle stationary',
        type: 'boolean',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_DROP'],
    },
    {
        path: 'event.attributes.ignition',
        label: 'ignition',
        type: 'string',
        scope: 'ANYWHERE',
        eventTypes: ['FUEL_DROP'],
    },
    {
        path: 'event.attributes.ignitionOn',
        label: 'ignition on',
        type: 'boolean',
        scope: 'ANYWHERE',
        /*
         * The security signals as well as the alarms, and on a tow it is the field
         * that changes what the event MEANS: a vehicle moved with the ignition on
         * is a recovery by somebody holding the key, with it off it is not.
         */
        eventTypes: [...ALARM, 'TOW_ALERT', 'IMMOBILISER_ENGAGED', 'IMMOBILISER_DISENGAGED'],
        // Emitted only when the sample carried an ignition reading.
        conditional: true,
    },
    {
        path: 'event.attributes.method',
        label: 'how it was operated',
        /*
         * A STRING, not an enum, and that is the provider's vocabulary rather than
         * ours. AjnaView sends whatever its firmware calls the action — a key fob,
         * a remote command from the office, a workshop tool — and enumerating a set
         * we have not confirmed would offer an author options the events never
         * carry while hiding the ones they do.
         */
        type: 'string',
        scope: 'ANYWHERE',
        eventTypes: ['IMMOBILISER_ENGAGED', 'IMMOBILISER_DISENGAGED'],
        // Only when the provider said. Most immobiliser alerts carry nothing.
        conditional: true,
    },
    /*
     * ⚠️ **`event.measurements.odometerRawFromProvider` IS DELIBERATELY ABSENT.**
     *
     * A catalogue entry must declare a `unit`, and the unit of the provider's
     * odometer is NOT CONFIRMED — the sample reads 3.36353132E8, which is 336,353
     * km if these are metres (Traccar's own storage unit) and absurd if they are
     * kilometres. Declaring one on an assumption is the `acceleration`-as-m/s²
     * mistake, and here it would label a number on screen and in the policy editor
     * with a unit nobody checked.
     *
     * The uncatalogued fallback is exactly the honest behaviour: the path renders
     * from its own name, which already says `RawFromProvider`, and **no unit is
     * invented**. Catalogue it the day AjnaView answers, not before.
     */
    {
        path: 'event.attributes.generatedType',
        label: 'what triggered the service',
        /*
         * A STRING rather than an enum, the same call `event.attributes.method`
         * makes: the sample carries `odometer`, and the rest of the provider's
         * vocabulary — hours, days, engine hours — is unconfirmed. Enumerating a set
         * we have not seen would offer an author options the events never carry.
         */
        type: 'string',
        scope: 'ANYWHERE',
        eventTypes: ['MAINTENANCE_DUE'],
        conditional: true,
    },
    {
        path: 'event.attributes.maintenanceId',
        /*
         * The provider's own reference for the service item. Catalogued so a policy
         * can single one out — "brake inspections are urgent, oil changes are not" —
         * which is the only way a tenant can express that, since OIP holds no
         * service definitions of its own.
         */
        label: 'the provider’s maintenance reference',
        type: 'string',
        scope: 'ANYWHERE',
        eventTypes: ['MAINTENANCE_DUE'],
        conditional: true,
    },
    {
        path: 'event.attributes.motionEvidence',
        label: 'evidence the vehicle was moving',
        type: 'enum',
        scope: 'ANYWHERE',
        options: ['SPEED', 'IGNITION', 'NONE'],
        eventTypes: ALARM,
    },
    {
        path: 'event.attributes.occupant',
        label: 'occupant',
        type: 'enum',
        scope: 'ANYWHERE',
        options: ['DRIVER', 'PASSENGER'],
        eventTypes: SEATBELT,
    },
    {
        path: 'event.attributes.minSpeedKph',
        label: 'the speed threshold that produced this',
        type: 'number',
        unit: ' km/h',
        scope: 'ANYWHERE',
        eventTypes: SEATBELT,
    },
    {
        path: 'event.attributes.requiredSustainedSeconds',
        label: 'the duration threshold that produced this',
        type: 'number',
        unit: ' seconds',
        scope: 'ANYWHERE',
        eventTypes: SEATBELT,
    },
    // ── surrounding context ───────────────────────────────────────────────────
    {
        path: 'context.vehicleEventCountLast24h',
        label: 'similar events for this vehicle in 24 hours',
        type: 'number',
        scope: 'ANYWHERE',
    },
    { path: 'vehicle.name', label: 'vehicle name', type: 'string', scope: 'ANYWHERE' },
    // ── correlation factors: real, useful, and not guaranteed ─────────────────
    {
        path: 'correlation.classification',
        label: 'classification',
        type: 'string',
        scope: 'ANYWHERE',
        eventTypes: ['BATTERY_POWER_ZERO'],
        conditional: true,
    },
    /*
     * Emitted by the alarm rule (`telemetry-safety.rule.ts`) as well as the
     * battery rule. Narrowing these to BATTERY_POWER_ZERO alone was wrong, and
     * wrong in the direction that matters: the shipped alarm policies read them,
     * so the write guard would have refused a new version of a policy that has
     * been working — a catalogue refusing a field that works, which is the exact
     * failure this catalogue exists to avoid. Caught by the seed parity test.
     */
    {
        path: 'correlation.ignitionOn',
        label: 'ignition on (correlation)',
        type: 'boolean',
        scope: 'ANYWHERE',
        eventTypes: ['BATTERY_POWER_ZERO', ...ALARM],
        conditional: true,
    },
    {
        path: 'correlation.motionEvidence',
        label: 'evidence the vehicle was moving (correlation)',
        type: 'string',
        scope: 'ANYWHERE',
        eventTypes: ['BATTERY_POWER_ZERO', ...ALARM],
        conditional: true,
    },
    {
        path: 'correlation.raisedWithoutCorroboration',
        label: 'raised with nothing corroborating it',
        type: 'boolean',
        scope: 'ANYWHERE',
        eventTypes: ['IMMOBILISER_DISENGAGED'],
        /*
         * ⚠️ Load-bearing, not descriptive. A release a tow or an alarm corroborates
         * MERGES onto that incident, and the engine's `updateRisk` takes the newest
         * assessment unconditionally — so a merged release scored at its own band
         * would pull a tow's 95 down to 65, new evidence making a theft look
         * milder. `IMMOBILISER_RELEASE_POLICY_V1` is conditioned on this being
         * true, so it matches the standalone case only.
         */
        conditional: true,
    },
    {
        path: 'correlation.immobiliserReleased',
        label: 'immobiliser released (correlation)',
        type: 'boolean',
        scope: 'ANYWHERE',
        eventTypes: ['TOW_ALERT'],
        // Set by the tow rule when a release is in the window. A tow alone can be a
        // recovery; a tow after a defeated immobiliser cannot.
        conditional: true,
    },
    {
        path: 'correlation.geofenceId',
        label: 'the geofence (correlation)',
        type: 'string',
        scope: 'ANYWHERE',
        eventTypes: ['GEOFENCE_EXIT'],
        conditional: true,
    },
    /*
     * ── the trip the event fell inside ────────────────────────────────────────
     *
     * The reason trips exist (`trip-context-design.md` §7). They let a customer
     * write the rule the whole feature was built for:
     *
     *   harsh braking in the final 30 minutes of a trip over four hours
     *   → escalate as fatigue
     *
     * **Every one is `conditional`, and that is not a formality.** An event
     * outside every trip is NORMAL — a tow, a break-in, a battery alarm on a
     * parked vehicle — and so is a vehicle that reports neither ignition nor
     * speed, which gets no trips at all by design (D2). A rule reading `trip.*`
     * on those resolves to undefined and lands in the backtest's UNDECIDABLE
     * bucket, which is the honest answer rather than a silent non-match.
     *
     * They are readable ANYWHERE because the trip is known at evaluation time:
     * derivation runs inline at ingestion, so the trip is already open when the
     * event correlates (D10). A design that ingested completed trips instead
     * could not put these here at all.
     */
    {
        path: 'trip.durationMinutes',
        label: 'how long the trip lasted (minutes)',
        type: 'number',
        scope: 'ANYWHERE',
        // Absent on a trip still IN PROGRESS, which is the common case at
        // evaluation time — a trip has no duration until it ends. `trip.inProgress`
        // is how a rule tells that apart from "no trip at all".
        conditional: true,
    },
    {
        path: 'trip.distanceKm',
        label: 'how far the trip has gone (km)',
        type: 'number',
        scope: 'ANYWHERE',
        // Also absent when the vehicle measures no distance — neither odometer nor
        // position. Null rather than zero: a distance of zero is a vehicle that did
        // not move, and `trip.distanceKm < 5` must not match a vehicle we cannot
        // speak for.
        conditional: true,
    },
    {
        path: 'trip.elapsedMinutes',
        label: 'how far into the trip the event fell (minutes)',
        type: 'number',
        scope: 'ANYWHERE',
        conditional: true,
    },
    {
        path: 'trip.remainingMinutes',
        label: 'how long was left of the trip (minutes)',
        type: 'number',
        scope: 'ANYWHERE',
        /*
         * Null on an open trip, and that is the whole care taken here. A trip in
         * progress has no end, so "how long is left" is unknowable; returning zero
         * would read as "the trip just ended" and fire the fatigue rule on every
         * event of every journey.
         */
        conditional: true,
    },
    {
        path: 'trip.eventCount',
        label: 'events already on this trip',
        type: 'number',
        scope: 'ANYWHERE',
        conditional: true,
    },
    {
        path: 'trip.indexToday',
        label: 'which trip of the vehicle’s day this is',
        type: 'number',
        scope: 'ANYWHERE',
        conditional: true,
    },
    {
        path: 'trip.inProgress',
        label: 'the trip had not ended yet',
        type: 'boolean',
        scope: 'ANYWHERE',
        /*
         * Exists so a rule can tell an OPEN trip from NO trip.
         *
         * Without it both look identical from a condition on `trip.durationMinutes`
         * — undefined either way — and those are opposite facts: one is a journey
         * under way, the other is a vehicle being towed.
         */
        conditional: true,
    },
    // ── risk: action rules only ───────────────────────────────────────────────
    {
        path: 'risk.score',
        label: 'risk score',
        type: 'number',
        scope: 'ACTION_RULES_ONLY',
    },
    {
        path: 'risk.level',
        label: 'risk level',
        type: 'enum',
        scope: 'ACTION_RULES_ONLY',
        options: ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'],
    },
    // ── baseline deviation: what the anomaly engine concluded ─────────────────
    /*
     * ⚠️ ONE entry per path. `BY_PATH` is a Map keyed on it, so a second row for
     * an existing path silently SHADOWS the first — which has already broken every
     * seatbelt policy once.
     *
     * All conditional: an event outside the anomaly pipeline carries none of
     * these, and a policy reading one elsewhere resolves to undefined and simply
     * never matches.
     */
    /*
     * Which family raised this — the unit a tenant tunes, and what the stock
     * policy bands on so ONE policy covers every family without fifteen event
     * types.
     */
    {
        path: 'event.attributes.family',
        label: 'anomaly family',
        type: 'enum',
        scope: 'ANYWHERE',
        options: ['DISTANCE', 'OPERATING_TIME', 'DRIVE_DURATION', 'IDLE', 'ACTIVITY', 'CONNECTIVITY'],
        eventTypes: ['BASELINE_DEVIATION'],
        conditional: true,
    },
    /*
     * How unusual the observation was, 0 to 1, from its position in this
     * vehicle's own baseline.
     *
     * ⚠️ NOT importance. It says how far from normal, never how much it matters —
     * that is the policy's to decide, which is exactly why they are two numbers
     * and not one.
     */
    {
        path: 'event.attributes.detectionScore',
        label: 'detection score',
        type: 'number',
        scope: 'ANYWHERE',
        eventTypes: ['BASELINE_DEVIATION'],
        conditional: true,
    },
    /*
     * How far the baseline behind this could be trusted — sample count, spread,
     * recency and cohort rung. A gate rather than a multiplier: an event reaches a
     * policy only because it already passed the floor.
     */
    {
        path: 'event.attributes.reliability',
        label: 'baseline reliability',
        type: 'number',
        scope: 'ANYWHERE',
        eventTypes: ['BASELINE_DEVIATION'],
        conditional: true,
    },
    /*
     * ⚠️ Which side of normal, and it is rarely decoration. A vehicle doing four
     * times its usual distance and one doing none are both deviations and are
     * entirely different conversations.
     */
    {
        path: 'event.attributes.direction',
        label: 'direction',
        type: 'enum',
        scope: 'ANYWHERE',
        options: ['ABOVE', 'BELOW'],
        eventTypes: ['BASELINE_DEVIATION'],
        conditional: true,
    },
    /*
     * The measurement itself, in the feature's canonical unit. ⚠️ Its meaning
     * depends on the family, so a threshold on it alone is safe only alongside a
     * family condition — 400 is a long day in kilometres and an impossible one in
     * minutes.
     */
    {
        path: 'event.attributes.observed',
        label: 'observed value',
        type: 'number',
        scope: 'ANYWHERE',
        eventTypes: ['BASELINE_DEVIATION'],
        conditional: true,
    },
    /* What this vehicle usually does, in the same unit as the observed value. */
    {
        path: 'event.attributes.baselineMedian',
        label: 'usual value',
        type: 'number',
        scope: 'ANYWHERE',
        eventTypes: ['BASELINE_DEVIATION'],
        conditional: true,
    },
];
const BY_PATH = new Map(POLICY_FIELDS.map((f) => [f.path, f]));
export function policyField(path) {
    return BY_PATH.get(path);
}
/**
 * The fields offerable in one place of one policy.
 *
 * `eventType` narrows to the payload that event actually carries — offering
 * `overByKph` on a fuel policy would be offering a condition that can never
 * match. `section` applies the risk rule.
 */
export function fieldsFor(eventType, section) {
    return POLICY_FIELDS.filter((f) => {
        if (f.scope === 'ACTION_RULES_ONLY' && section !== 'ACTION')
            return false;
        return !f.eventTypes || f.eventTypes.includes(eventType);
    });
}
/**
 * Why this field cannot be used here, or undefined when it can.
 *
 * Returns a reason rather than a boolean so the refusal can be shown. An
 * unrecognised path is deliberately NOT an error: definitions written before
 * this catalogue existed must still validate, and a path the catalogue has not
 * caught up with should not become unusable because of a release ordering.
 */
export function fieldRefusal(path, eventType, section) {
    const field = BY_PATH.get(path);
    if (!field)
        return undefined;
    if (field.scope === 'ACTION_RULES_ONLY' && section !== 'ACTION') {
        return `“${field.label}” can only be used in a recommendation rule. Risk is calculated from the score bands, so it does not exist yet while they are being evaluated — a condition on it here would never match.`;
    }
    if (field.eventTypes && !field.eventTypes.includes(eventType)) {
        return `“${field.label}” is not carried by ${eventType} events, so a condition on it would never match.`;
    }
    return undefined;
}
export const policyFieldSchema = z.object({
    path: z.string(),
    label: z.string(),
    type: z.enum(['number', 'string', 'boolean', 'enum']),
    unit: z.string().optional(),
    options: z.array(z.string()).optional(),
    scope: z.enum(FIELD_SCOPES),
    eventTypes: z.array(z.string()).optional(),
    conditional: z.literal(true).optional(),
});
/**
 * Recommendation actions an author is likely to want — SUGGESTIONS, not a
 * vocabulary.
 *
 * `actionCategory` is `z.string().min(1)` and nothing downstream switches on it;
 * every screen simply title-cases it for display. So this is deliberately not an
 * enum, and the editor offers it as free text with these as suggestions.
 *
 * The first attempt was a closed dropdown with a hand-written list, and it did
 * exactly what a hand-written list does: it missed four categories the shipped
 * policies actually use, and MUI rendered those real values as an EMPTY select —
 * a rule the author could not read, on the screen built to make rules readable.
 * It also invented one nothing uses.
 *
 * `apps/api/test/policy-fields.test.ts` now asserts every category the seeded
 * policies use appears here. That keeps the suggestions honest without making
 * them a constraint the contract does not have.
 */
export const SUGGESTED_ACTION_CATEGORIES = [
    'REVIEW',
    'REVIEW_BEHAVIOUR',
    'COACH_DRIVER',
    'CONTACT_DRIVER',
    'ESCALATE',
    'INVESTIGATE',
    'INSPECT_VEHICLE',
    'INSPECT_DEVICE',
    'SCHEDULE_MAINTENANCE',
    'CHECK_AUTHORISATION',
    'VERIFY_ASSIGNMENT',
    /*
     * ⚠️ The category that means "worth recording, not worth chasing" (design
     * D21). It is what makes an incident's obligation class INFORMATIONAL, and
     * without it every baseline anomaly is ACTIONABLE — the fatigue problem
     * moving from Fleet News into the queue rather than resolving.
     */
    'NO_ACTION_NEEDED',
];
//# sourceMappingURL=policy-fields.js.map